import { useEffect, useRef } from "react";

export default function StarfieldCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationId: number;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    // High DPI Retina Support
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    interface StarNode {
      x: number;
      y: number;
      baseX: number;
      baseY: number;
      size: number;
      speedX: number;
      speedY: number;
      opacity: number;
      twinkleSpeed: number;
      phase: number;
      depth: number;
      color: string;
      update: (mouseX: number, mouseY: number) => void;
      draw: () => void;
    }

    const stars: StarNode[] = [];
    const starCount = 100;

    class Star implements StarNode {
      x: number;
      y: number;
      baseX: number;
      baseY: number;
      size: number;
      speedX: number;
      speedY: number;
      opacity: number;
      twinkleSpeed: number;
      phase: number;
      depth: number;
      color: string;

      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.baseX = this.x;
        this.baseY = this.y;
        this.size = Math.random() * 1.6 + 0.4;
        this.speedX = (Math.random() - 0.5) * 0.06;
        this.speedY = (Math.random() - 0.5) * 0.06;
        this.opacity = Math.random() * 0.4 + 0.2;
        this.twinkleSpeed = Math.random() * 0.012 + 0.004;
        this.phase = Math.random() * Math.PI * 2;
        this.depth = Math.random() * 0.8 + 0.2; // Parallax weighting factor

        // 80% Violet/Purple stars, 20% warm gold/amber stars for celestial harmony
        this.color = Math.random() > 0.2 
          ? "139, 92, 246" // Violet (#8b5cf6)
          : "240, 180, 41"; // Amber (#f0b429)
      }

      update(mouseX: number, mouseY: number) {
        this.phase += this.twinkleSpeed;

        // Dynamic mouse offset based on depth level
        const targetOffsetX = (mouseX - width / 2) * 0.01 * this.depth;
        const targetOffsetY = (mouseY - height / 2) * 0.01 * this.depth;

        this.x += this.speedX;
        this.y += this.speedY;

        // Boundaries loop wrapping
        if (this.x < 0) this.x = width;
        if (this.x > width) this.x = 0;
        if (this.y < 0) this.y = height;
        if (this.y > height) this.y = 0;

        // Apply mouse-driven structural parallax smoothly
        this.baseX = this.x + targetOffsetX;
        this.baseY = this.y + targetOffsetY;
      }

      draw() {
        const dynamicTwinkle = Math.max(0.1, this.opacity + Math.sin(this.phase) * 0.25);
        ctx!.fillStyle = `rgba(${this.color}, ${dynamicTwinkle * 0.75})`;
        ctx!.beginPath();
        ctx!.arc(this.baseX, this.baseY, this.size, 0, Math.PI * 2);
        ctx!.fill();
      }
    }

    // Initialize Star Population
    for (let i = 0; i < starCount; i++) {
      stars.push(new Star());
    }

    let mouseX = width / 2;
    let mouseY = height / 2;
    let currentMouseX = width / 2;
    let currentMouseY = height / 2;

    const handleMouseMove = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };
    window.addEventListener("mousemove", handleMouseMove);

    const handleResize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);
    };
    window.addEventListener("resize", handleResize);

    // Core Proximity Swarm Constellation Lines Visualizer
    const drawConstellationNetwork = () => {
      ctx.lineWidth = 0.35;
      for (let i = 0; i < stars.length; i++) {
        for (let j = i + 1; j < stars.length; j++) {
          const dx = stars[i].baseX - stars[j].baseX;
          const dy = stars[i].baseY - stars[j].baseY;
          const dist = Math.sqrt(dx * dx + dy * dy);

          // Render link if within distance budget
          if (dist < 110) {
            const proximityAlpha = (1 - dist / 110) * 0.07;
            
            // Blend colors between connecting nodes (primarily violet-indigo)
            ctx.strokeStyle = `rgba(139, 92, 246, ${proximityAlpha})`;
            ctx.beginPath();
            ctx.moveTo(stars[i].baseX, stars[i].baseY);
            ctx.lineTo(stars[j].baseX, stars[j].baseY);
            ctx.stroke();
          }
        }
      }
    };

    const renderLoop = () => {
      ctx.clearRect(0, 0, width, height);

      // Organic Lerp for mouse inertia smoothing
      currentMouseX += (mouseX - currentMouseX) * 0.05;
      currentMouseY += (mouseY - currentMouseY) * 0.05;

      stars.forEach((star) => {
        star.update(currentMouseX, currentMouseY);
        star.draw();
      });

      drawConstellationNetwork();
      animationId = requestAnimationFrame(renderLoop);
    };

    renderLoop();

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full pointer-events-none z-0"
      style={{ mixBlendMode: "screen", opacity: 0.8 }}
    />
  );
}
